import { Component, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs';

import { punktLabel, istPunktartMitProtokoll, idOf, AufnahmePunktDocument, punktartBeschreibung, punktartSvgUrl, istPunktartOhneSymbol, punktartOhneSymbolLabel, punktartOhneSymbolColor } from '../../data-model';
import { personenangabenVerbergen } from '../personenangaben';
import { ValidationLevel as ValRes, StorageService, SelectionService, BaseListPanelComponent, FILTER_PROVIDER, pointStatusBadge, StatusBadge, STATUS_BADGES } from '@luzula/luzula-lib';
import { DeleteConfirmationService } from '../../shared/delete-confirmation/delete-confirmation.service';
import { AuthService } from 'src/app/auth.service';
import { DataService } from 'src/app/data.service';
import { FilterService } from 'src/app/filter.service';
import { ProjectService } from 'src/app/project.service';
import { ProjectPointsService, WdFilterComponent as FilterComponent } from '@luzula/luzula-lib';
import { ToastrService } from 'ngx-toastr';

/**
 * Farb-Legende für den Status-Punkt (die farbige Kugel je Listenzeile) im
 * Statistik-Hilfe-Modal — dieselben Farben/Labels wie in der Liste
 * (`STATUS_BADGES`), in Workflow-Reihenfolge. Modul-Konstante, damit die
 * Vollständigkeit ohne Komponenten-DI testbar ist.
 */
export const STATUS_DOT_LEGEND: StatusBadge[] = [
	STATUS_BADGES.anstehend,
	STATUS_BADGES.bearbeitung,
	STATUS_BADGES.kontrolle,
	STATUS_BADGES.in_pruefung,
	STATUS_BADGES.abgeschlossen,
	STATUS_BADGES.abgelehnt,
];

@Component({
	standalone: false,
	selector: 'bp-bohrpunkt-list-panel',
	templateUrl: './bohrpunkt-list-panel.component.html',
	styleUrls: ['./bohrpunkt-list-panel.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class BohrpunktListPanelComponent extends BaseListPanelComponent {
	private router = inject(Router);

	@ViewChild('statsHelpModal') statsHelpModal: any;
	@ViewChild('settingsModal') settingsModal: any;

	punktLabel = punktLabel;
	idOf = idOf;
	istPunktartMitProtokoll = istPunktartMitProtokoll;
	istPunktartOhneSymbol = istPunktartOhneSymbol;
	punktartOhneSymbolLabel = punktartOhneSymbolLabel;
	punktartOhneSymbolColor = punktartOhneSymbolColor;

	aus = inject(AuthService);
	private deleteConfirmation = inject(DeleteConfirmationService);
	private ds = inject(DataService);
	private ps = inject(ProjectService);
	private msg = inject(ToastrService);
	/** Konkrete FilterService-Instanz (= FILTER_PROVIDER) für das Ad-hoc-Drill-Ereignis. */
	private filterSvc = inject(FilterService);

	constructor() {
		super(inject(SelectionService), inject(FILTER_PROVIDER), inject(StorageService), inject(ProjectPointsService));
	}

	override ngOnInit(): void {
		super.ngOnInit();
		// Drill-down aus der Kontroll-Auswertung: Liste auf die übergebenen Punkt-IDs
		// filtern (benannter Ad-hoc-Filter → „×"-Reset + Auswahl-Kopplung wie sonst).
		this.filterSvc.applyAdHocView
			.pipe(takeUntil(this.destroy$))
			.subscribe(({ label, ids }) => this.applyAdHocFilter(label, ids));
	}

	/**
	 * Wendet eine Ad-hoc-ID-Liste als benannten Filter an (analog
	 * `applyProjectPointsFilter`): setzt `currentFilterName` (zeigt „×"-Reset +
	 * Funnel-Label), filtert die Liste und übernimmt die Treffer in die Auswahl.
	 * Bewusst NICHT in `storage` persistiert — der Drill ist transient.
	 */
	applyAdHocFilter(label: string, ids: string[]): void {
		this.currentFilterName = label;
		this.fs.setFilteredDocIds(ids);
		this.applyFilterSelectionOp('replace');
		this.updateFilteredCount();
		this.updateVisibleBps();
		this.filterApplied.emit(label);
		this.cd.markForCheck();
	}

	// --- Implementierung der abstrakten Methoden ---

	getItemId(bp: any): string {
		return bp._id;
	}

	protected hasProtocol(bp: any): boolean {
		return istPunktartMitProtokoll(bp.type);
	}

	protected matchesSearch(bp: any): boolean {
		if (!this.searchText || this.searchText.trim() === '') return true;

		const searchTerms = this.searchText.trim().split(/\s+/).filter(t => t.length > 0);
		if (searchTerms.length === 0) return true;

		const label = String(this.punktLabel(bp, true) || '').toLowerCase();
		const nummer = String(bp.header?.nummer || '').toLowerCase();
		const forstamt = String(bp.header?.forstamt || '').toLowerCase();
		const revier = bp.header?.revier?.toLowerCase() || '';
		// Verborgene Personenangaben (Datennutzer, nicht angemeldet) sind auch nicht
		// suchbar — sonst verriete ein Treffer den Namen oder das Aufnahmejahr.
		const personenVerborgen = personenangabenVerbergen(this.aus);
		const kartierer = personenVerborgen ? '' : (bp.header?.aufnahme?.kartierer?.toLowerCase() || '');
		const type = bp.type?.toLowerCase() || '';
		const datumRaw = personenVerborgen ? '' : (bp.header?.aufnahme?.datum || '');
		const jahr = datumRaw ? new Date(datumRaw).getFullYear().toString() : '';
		const vegetation = bp.header?.vegetationsformengruppe?.toLowerCase() || '';

		const bfArray = bp.header?.bodenform?.bf || [];
		const fbfValues = bfArray.map((b: any) => b.fbf || '').join(' ');
		const hbfValues = bfArray.map((b: any) => b.hbf || '').join(' ');
		const hf = bp.header?.humusform?.aufnahmeFlaeche?.neu || bp.header?.humusform?.aufnahmeFlaeche?.alt || '';

		return searchTerms.every(term => {
			const termLower = term.toLowerCase();
			const matchesInsensitive =
				label.includes(termLower) ||
				nummer.includes(termLower) ||
				forstamt.includes(termLower) ||
				revier.includes(termLower) ||
				kartierer.includes(termLower) ||
				type.includes(termLower) ||
				jahr.includes(termLower) ||
				vegetation.includes(termLower);
			const matchesSensitive =
				fbfValues.includes(term) ||
				hbfValues.includes(term) ||
				hf.includes(term);
			return matchesInsensitive || matchesSensitive;
		});
	}

	// Werte immer erst in Text wandeln: Manche Punkte tragen ihre Nummer als Zahl.
	// Ein einziger solcher Punkt liess sonst die ganze Sortierung abbrechen, und die
	// Liste blieb leer (Befund 29.09.2026, sortierung-nummer-als-zahl.spec.ts).
	protected compareBps(a: any, b: any, sortOrder: string): number {
		switch (sortOrder) {
			case 'type':
				return idOf(a.type) - idOf(b.type);
			case 'nummer':
				const numA = String(a.header?.nummer ?? '');
				const numB = String(b.header?.nummer ?? '');
				return numA.localeCompare(numB, 'de', { numeric: true });
			case 'kartierer':
				const kartA = String(a.header?.aufnahme?.kartierer ?? '');
				const kartB = String(b.header?.aufnahme?.kartierer ?? '');
				return kartA.localeCompare(kartB, 'de');
			case 'datum':
				const datumA = String(a.header?.aufnahme?.datum ?? '');
				const datumB = String(b.header?.aufnahme?.datum ?? '');
				return datumB.localeCompare(datumA); // Neueste zuerst
			default:
				return 0;
		}
	}

	// --- App-spezifische Methoden ---

	applyQuickFilter(name: string) {
		this.currentFilterName = name;
		this.storage.setSync('currentFilter', name);

		this.fs.get(name).then((conditions) => {
			if (conditions) {
				// Filter immer als reine Bedingungs-Treffer bauen — KEINE Bounds und
				// KEINE Auswahl einbacken. Der Bereich (Alle/Karte/Auswahl) wird live
				// in isVisibleInList zusaetzlich angewandt (Schnittmenge). Das haelt
				// die Bereichs-Buttons auch bei aktivem Filter wirksam und korrekt beim
				// Moduswechsel (frueher zeigte "Alle" nach Karte/Auswahl-Apply die
				// eingebackene Teilmenge statt aller Filter-Treffer).
				this.fs.storeViews(name, conditions, false, [], () => {
					// Anwenden eines gespeicherten Filters setzt die Auswahl auf die
					// Treffer (Filter füllt die Auswahl). Erst NACH applyFilter, weil
					// dort fs.filteredDocIds gefüllt wird.
					this.fs.applyFilter(name).then(() => this.applyFilterSelectionOp('replace'));
					this.mapFilterStale = false;
					this.filterApplied.emit(name);
				});
			} else {
				this.currentFilterName = null;
				this.storage.removeSync('currentFilter');
			}
		});
	}

	openFilterEditor() {
		const modalRef = this.modalService.open(FilterComponent, { size: 'lg' });
		const inst = modalRef.componentInstance;
		inst.local = true;
		inst.filterRect = this.currentBounds;

		inst.filterApplied.subscribe((filterName: string) => {
			this.currentFilterName = filterName;
			this.fs.get(filterName).then((conditions) => {
				if (conditions) {
					this.fs.storeViews(filterName, conditions, false, [], () => {
						// Nach dem Anwenden stehen die Treffer in fs.filteredDocIds — per
						// im Modal gewählter Operation (selectionOp) in die Auswahl übernehmen.
						this.fs.applyFilter(filterName).then(() => this.applyFilterSelectionOp(inst.selectionOp));
						this.filterApplied.emit(filterName);
					});
				}
			});
		});
	}

	async deleteSelection() {
		const count = this.selectionService.count();
		if (count === 0) {
			this.msg.info('Keine Punkte ausgewählt');
			return;
		}

		// Sammle die ausgewählten Punkte für die erweiterte Bestätigung
		const selectedPoints = this.bps.filter(bp => this.selectionService.isSelected(bp._id));

		// Per-Punkt-Gate (Matrix §4.2 B8): nur eigene Punkte in `bearbeitung` (bzw.
		// Owner/Entwickler). Fremde/eingefrorene/in Kontrolle werden übersprungen,
		// nicht stillschweigend gelöscht — analog alle-bohrpunkte.deleteSelection.
		const deletable = selectedPoints.filter(bp => this.ps.canIRemovePoint(bp));
		const skipped = selectedPoints.length - deletable.length;
		if (deletable.length === 0) {
			this.msg.warning('Keine der ausgewählten Punkte darf gelöscht werden (nur eigene Punkte in Bearbeitung).');
			return;
		}

		const confirmed = await this.deleteConfirmation.confirmDeleteBulkFast(deletable);
		if (!confirmed) return;

		await this.ds.removeBohrpunkteBulk(deletable.map(bp => bp._id));
		this.selectionService.clear();
		if (skipped > 0) {
			this.msg.warning(`${skipped} Punkt${skipped > 1 ? 'e' : ''} übersprungen (keine Löschrechte)`);
		}
		this.pointsDeleted.emit();
	}

	openSettings() {
		this.modalService.open(this.settingsModal, { centered: true });
	}

	showStatsHelp() {
		this.modalService.open(this.statsHelpModal, { size: 'sm', centered: true });
	}

	// Hilfsmethoden für die Listendarstellung
	getSymbolNummer(bp: AufnahmePunktDocument): number {
		return idOf(bp.type);
	}

	getTypName(bp: AufnahmePunktDocument): string {
		return punktartBeschreibung(bp.type);
	}

	/**
	 * Was die Suche wirklich durchsucht. Fuer Datennutzer stehen Kartierer und
	 * Aufnahmejahr nicht darin — der Hinweis darf sie dann auch nicht nennen.
	 */
	get suchHinweis(): string {
		return personenangabenVerbergen(this.aus)
			? 'Sucht in: Nummer, Forstamt, Revier, Typ, Vegetation, FBF, HBF, Humusform'
			: 'Sucht in: Nummer, Forstamt, Revier, Kartierer, Typ, Jahr, Vegetation, FBF, HBF, Humusform';
	}

	getKartierer(bp: AufnahmePunktDocument): string {
		return bp.header?.aufnahme?.kartierer || '';
	}

	getKurzinfoRed(bp: AufnahmePunktDocument): string {
		let result = '';
		const bodenform = bp.header?.bodenform;
		const bf = bodenform?.bf;
		const hasPerPairPrefix = bf?.some(b => !!b.prefix);
		const legacyPrefix = !hasPerPairPrefix ? bodenform?.prefix : undefined;
		const hasFbf = bf?.some(b => !!b.fbf);
		if (hasFbf) {
			if (bf && bf.length > 0) {
				const first = bf[0];
				const firstPrefix = first.prefix || legacyPrefix || '';
				result += firstPrefix + first.fbf;
				for (let i = 1; i < bf.length; i++) {
					const p = bf[i];
					if (!p.fbf) continue;
					if (p.trenner) result += p.trenner;
					if (p.prefix) result += p.prefix;
					result += p.fbf;
				}
			}
		} else {
			// Keine FBF: HBF anzeigen
			bf?.forEach((b, i) => {
				if (!b.hbf) return;
				if (i > 0 && b.trenner) result += ' ' + b.trenner;
				result += (result.length > 0 ? ' ' : '') + b.hbf;
			});
		}
		if (bodenform?.suffix) result += bodenform.suffix;
		// 7er-Wasserform nicht an FBF anhängen
		const ws2 = bp.header?.wasserstufe2 == '7' ? null : bp.header?.wasserstufe2;
		const ws = bp.header?.wasserstufe == '7' ? null : bp.header?.wasserstufe;
		if (ws2) result += ws2;
		if (ws) result += ws;
		if (bodenform?.reliktG) result += '(G)';
		return result;
	}

	getKurzinfoGreen(bp: AufnahmePunktDocument): string {
		return bp.header?.humusform?.aufnahmeFlaeche?.neu || bp.header?.humusform?.aufnahmeFlaeche?.alt || '';
	}

	getSvgUrl(bp: AufnahmePunktDocument): string {
		return punktartSvgUrl(bp.type);
	}

	openProtokoll(event: MouseEvent, bp: AufnahmePunktDocument) {
		event.stopImmediatePropagation();
		event.preventDefault();
		this.router.navigateByUrl('/bohrpunkte/' + bp._id);
	}

	getCommentClass(bp: any): string {
		const severity = bp.state?.commentsSeverity;
		if (severity === ValRes.danger) return 'comment-danger';
		if (severity === ValRes.warning) return 'comment-warning';
		return '';
	}

	/**
	 * Workflow-Status-Badge für den farbigen Status-Punkt rechts in der Zeile.
	 * Identisch zur Detail-Kopfzeile (pointStatusBadge); Punkte ohne Workflow
	 * liefern das „Bestand"-Badge (hellgrau).
	 */
	getStatusBadge(bp: AufnahmePunktDocument): StatusBadge | null {
		return pointStatusBadge(bp.workflow);
	}

	/** Status-Punkt-Legende fürs Hilfe-Modal (siehe STATUS_DOT_LEGEND). */
	readonly statusLegend = STATUS_DOT_LEGEND;
}
